/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useNavigate } from 'js/lib/useRouter';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';
import { DownloadIcon } from '@coursera/cds-icons';
import { Button as TrackedButton } from '@coursera/event-pulse/react';

import { UsageReportDrawer } from 'bundles/enterprise-admin-drawers/components/UsageReportDrawer';
import type { ProgramNameList } from 'bundles/enterprise-admin-home/utils/ProgramSummaryUtils';
import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import { useModalState } from 'bundles/enterprise-ui/hooks';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  wrapper: css`
    background-color: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-50);
    border: 1px solid var(--cds-color-grey-50);
  `,
  button: css`
    ${breakpoints.up('md')} {
      margin-left: var(--cds-border-radius-200);
    }
  `,
};

type Props = {
  orgId: string;
  thirdPartySlug: string;
  programNameList: ProgramNameList;
};

const DownloadUsageReportCard = ({ orgId, thirdPartySlug, programNameList }: Props) => {
  const [isUsageReportOpen, show, hide] = useModalState(false);
  const enableSeparateReports = EnterpriseExperiments.get('enableSeparateReports').includes(thirdPartySlug);
  const navigate = useNavigate();
  const enterpriseAdminTrackingData = useEnterpriseAdminTrackingData();

  return (
    <Grid container direction="row" alignItems="center" justifyContent="space-between" css={styles.wrapper}>
      <Typography2 component="h3" variant="subtitleLarge">
        {_t('Download detailed reports about your learners')}
      </Typography2>
      {enableSeparateReports ? (
        <TrackedButton
          css={styles.button}
          variant="ghost"
          onClick={() => navigate(`/o/${thirdPartySlug}/admin/analytics/reports?source=ADMIN_HOME`)}
          data-testid="view-reports-button"
          trackingData={{
            ...enterpriseAdminTrackingData,
            button: {
              name: 'enterprise_admin_view_reports',
            },
          }}
        >
          {_t('View reports')}
        </TrackedButton>
      ) : (
        <TrackedButton
          css={styles.button}
          iconPosition="before"
          variant="ghost"
          onClick={show}
          icon={<DownloadIcon />}
          data-testid="usage-report-button"
          trackingData={{
            ...enterpriseAdminTrackingData,
            button: {
              name: 'enterprise_admin_download_usage_report',
            },
          }}
        >
          {_t('Download usage report')}
        </TrackedButton>
      )}
      <UsageReportDrawer organizationId={orgId} programs={programNameList} onClose={hide} isOpen={isUsageReportOpen} />
    </Grid>
  );
};

export default DownloadUsageReportCard;
